<template>
  <div :class="[additionalClasses, 'variety-advisor']">
    <tabs @tab-selected="(data) => { tabSelected = data; }">
      <tab v-for="(crop, index) in crops" :key="crop.dashed" :id="crop.dashed" :title="crop.name" :class="[`theme-${crop.dashed} theme-${crop.dashed}--light`]">
        <div v-if="tabSelected === index" class="kws-tool__input-container full-width">
          <component :is="`crop-icon-${crop.dashed}`"></component>

          <postal-code-place
            v-if="!crop.locationDisabled"
            :postal-code-pattern="postalCodePattern"
            :postal-code-value="location.postalCode"
            :city-value="location.placeName"
            :country-code="tenant"
            :api-url="apiUrls.geoweather"
            :wrapper="GrowingRegion"
            :autocomplete="autocompletePlace"
            @change="changeLocation"
          ></postal-code-place>

          <p class="h4" v-if="regionOutput">{{ regionOutput }}</p>

          <triangle></triangle>
        </div>

        <div v-if="tabSelected === index" class="kws-tool__output-container full-width pb-5">
          <h2 class="h2" v-text="translate('lblResultsHeadline')"></h2>
          <result-view
            :preload-results="preloadCompleted || tabSelected === index"
            :api-url="apiUrls.pim"
            :tenant="tenant"
            :country-code="countryCode"
            :language="language"
            :crop="crop.id"
            :postal-code="location.postalCode"
            :default-settings="crop.defaultSettings || {}"
            :activate-comparison="crop.activateComparison"
            :activate-booking-link="crop.activateBookingLink"
            :activate-salesforce-form-link="crop.activateSalesforceFormLink"
            :activate-subheadline="crop.activateSubheadline"
            :max-nr-of-varieties-to-be-compared="maxNrOfVarietiesToBeCompared"
            :hint-visible-duration="hintVisibleDuration"
            :comparison-view="comparisonView"
            :booking-link="getBookingLink(crop)"
            :salesforce-form-link="getSalesforceFormLink(crop)"
            :comparison-selection-label="getSelectionLabel(crop)"
            :open-comparison-label="getOpenComparisonLabel(crop)"
            :comparison-view-link="getComparisonViewLink(crop)"
            :limit-number-of-results="limitNumberOfResults"
            :number-of-results-initially-shown="numberOfResultsInitiallyShown"
            :benchmarking-tool-link="crop.benchmarkingToolLink"
            @preload-completed="handlePreloadCompleted"
            @disable-location="disableLocation(crop)"
            @change-region-output="changeRegionOutput"
          ></result-view>
        </div>
      </tab>
    </tabs>
  </div>
</template>

<script>
import { slugify } from '../shared/Utils';
import Triangle from './components/icons/triangle.vue';
import Tabs from '../shared/components/tabs.vue';
import Tab from '../shared/components/tab.vue';
import ResultView from './components/result-view.vue';
import GrowingRegion from './components/growing-region.vue';
import PostalCodePlace from '../shared/components/postal-code-place.vue';
import CropIconCorn from './components/icons/crop-icon-corn.vue';
import CropIconWheat from './components/icons/crop-icon-wheat.vue';
import CropIconRye from './components/icons/crop-icon-rye.vue';
import CropIconBarley from './components/icons/crop-icon-barley.vue';
import CropIconOilseedRape from './components/icons/crop-icon-oilseed-rape.vue';
import CropIconCatchCrops from './components/icons/crop-icon-catch-crops.vue';
import CropIconOats from './components/icons/crop-icon-oats.vue';
import CropIconPea from './components/icons/crop-icon-pea.vue';
import CropIconSorghum from './components/icons/crop-icon-sorghum.vue';
import CropIconSugarBeet from './components/icons/crop-icon-sugar-beet.vue';
import CropIconSunflower from './components/icons/crop-icon-sunflower.vue';
import CropIconTriticale from './components/icons/crop-icon-triticale.vue';

export default {
  name: 'VarietyAdvisor',
  data: () => ({
    GrowingRegion,
    location: {
      postalCode: '',
      placeName: '',
    },
    regionOutput: '',
    tabSelected: -1,
    preloadCompleted: false,
  }),
  props: {
    additionalClasses: String,
    translations: Object,
    apiUrls: {
      type: Object,
      required: true,
    },
    tenant: {
      type: String,
      required: true,
    },
    countryCode: {
      type: String,
      required: true,
    },
    language: {
      type: String,
      required: true,
    },
    crops: {
      type: Array,
      required: true,
    },
    autocompletePlace: {
      type: Boolean,
      default: true,
    },
    comparisonView: String,
    lblSelectForComparison: String,
    bookingLink: String,
    salesforceFormLink: String,
    postalCodePattern: String,
    defaultPostalCode: String,
    defaultCity: String,
    maxNrOfVarietiesToBeCompared: {
      type: Number,
      required: false,
    },
    hintVisibleDuration: Number,
    limitNumberOfResults: Boolean,
    numberOfResultsInitiallyShown: Number,
  },
  methods: {
    changeLocation(data) {
      this.location = data;
    },
    changeRegionOutput(data) {
      this.regionOutput = data;
    },
    handlePreloadCompleted() {
      this.preloadCompleted = true;
    },
    getBookingLink(crop) {
      return crop.bookingLink || this.bookingLink;
    },
    getSalesforceFormLink(crop) {
      return crop.salesforceFormLink || this.salesforceFormLink;
    },
    getComparisonViewLink(crop) {
      return crop.comparisonView || this.comparisonView;
    },
    getSelectionLabel(crop) {
      return crop.lblSelectForComparison || this.translate('lblSelectForComparison');
    },
    getOpenComparisonLabel(crop) {
      return crop.lblOpenComparison || this.translate('lblOpenComparison');
    },
    disableLocation(crop) {
      crop.locationDisabled = true;
      this.$forceUpdate();
    },
  },
  created() {
    this.location.postalCode = window.KWS?.user?.profile?.address?.zip?.trim() || this.defaultPostalCode || '';
    this.location.placeName = window.KWS?.user?.profile?.address?.city || this.defaultCity || '';

    if (this.translations) {
      this.addDictionary(this.translations);
    }
    this.crops.forEach((c) => {
      // eslint-disable-next-line no-param-reassign
      c.dashed = slugify(c.id);
    });
  },
  components: {
    Triangle,
    Tabs,
    Tab,
    ResultView,
    PostalCodePlace,
    CropIconCorn,
    CropIconWheat,
    CropIconRye,
    CropIconBarley,
    CropIconOilseedRape,
    CropIconCatchCrops,
    CropIconSugarBeet,
    CropIconSorghum,
    CropIconSunflower,
    CropIconTriticale,
    CropIconPea,
    CropIconOats,
  },
};
</script>

<style lang="scss">
.variety-advisor {
  width: 100%;
}

p.h4 {
  display: inline-block;
  margin-top: 0.75rem;
}
</style>
